/**
 * Permission Helpers
 * Utility functions for checking and managing media permissions
 */

export type PermissionState = 'checking' | 'prompt' | 'denied' | 'granted' | 'error'

/**
 * Check permission state using Permissions API and getUserMedia
 */
export const checkPermissionState = async (): Promise<PermissionState> => {
  try {
    // First, try to check using Permissions API (if available)
    if ('permissions' in navigator) {
      try {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName })
        const microphonePermission = await navigator.permissions.query({ name: 'microphone' as PermissionName })

        if (cameraPermission.state === 'denied' || microphonePermission.state === 'denied') {
          return 'denied'
        }
      } catch {
        // Permissions API might not support camera/microphone in all browsers
        // Fall through to getUserMedia check
      }
    }

    // Try to get media stream to check if permissions are granted
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
    stream.getTracks().forEach((track) => track.stop())
    return 'granted'
  } catch (error) {
    const mediaError = error as DOMException

    if (mediaError.name === 'NotAllowedError' || mediaError.name === 'PermissionDeniedError') {
      return 'denied'
    }
    if (mediaError.name === 'NotFoundError' || mediaError.name === 'DevicesNotFoundError') {
      return 'error'
    }
    return 'prompt'
  }
}

/**
 * Get error message for a given DOMException
 */
export const getPermissionErrorMessage = (error: DOMException, t: (key: string) => string): string => {
  switch (error.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return t('gamer.permissionScreen.errors.permissionDenied')
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return t('gamer.permissionScreen.errors.devicesNotFound')
    case 'NotReadableError':
    case 'TrackStartError':
      return t('gamer.permissionScreen.errors.deviceInUse')
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return t('gamer.permissionScreen.errors.deviceNotSupported')
    default:
      return t('gamer.permissionScreen.errors.generic')
  }
}

const CAPTURE_GUARD_FLAG = '__intractionLateCaptureGuard'

type GuardedGetUserMedia = MediaDevices['getUserMedia'] & { [CAPTURE_GUARD_FLAG]?: boolean }

interface CallSurfaceCaptureOwner {
  isActive: boolean
  streams: Set<MediaStream>
}

export interface CallSurfaceCaptureRegistration {
  release: () => void
  stopCamera: () => void
}

const activeCallSurfaceCaptureOwners: CallSurfaceCaptureOwner[] = []

const stopCaptureTracks = (owner: CallSurfaceCaptureOwner, cameraOnly: boolean): void => {
  owner.streams.forEach((stream) => {
    const tracks = cameraOnly ? stream.getVideoTracks() : stream.getTracks()
    tracks.forEach((track) => track.stop())
  })
}

const currentCallSurfaceCaptureOwner = (): CallSurfaceCaptureOwner | undefined =>
  activeCallSurfaceCaptureOwners[activeCallSurfaceCaptureOwners.length - 1]

// The request remembers its exact call owner, so a replacement call cannot adopt a late stream.
const installLateCaptureGuard = (): void => {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return
  if ((navigator.mediaDevices.getUserMedia as GuardedGetUserMedia)[CAPTURE_GUARD_FLAG]) return

  const requestUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices)
  const guardedGetUserMedia: GuardedGetUserMedia = async (constraints?: MediaStreamConstraints) => {
    const captureOwner = currentCallSurfaceCaptureOwner()
    const stream = await requestUserMedia(constraints)
    const isCallCameraStream = captureOwner && stream.getVideoTracks().length > 0
    if (!isCallCameraStream) return stream

    if (captureOwner.isActive) {
      captureOwner.streams.add(stream)
    } else {
      stream.getTracks().forEach((track) => track.stop())
    }
    return stream
  }
  guardedGetUserMedia[CAPTURE_GUARD_FLAG] = true
  navigator.mediaDevices.getUserMedia = guardedGetUserMedia
}

export const registerCallSurfaceCapture = (): CallSurfaceCaptureRegistration => {
  installLateCaptureGuard()
  const captureOwner: CallSurfaceCaptureOwner = { isActive: true, streams: new Set() }
  activeCallSurfaceCaptureOwners.push(captureOwner)

  const release = () => {
    if (!captureOwner.isActive) return
    captureOwner.isActive = false
    stopCaptureTracks(captureOwner, false)
    const ownerIndex = activeCallSurfaceCaptureOwners.indexOf(captureOwner)
    if (ownerIndex >= 0) activeCallSurfaceCaptureOwners.splice(ownerIndex, 1)
  }

  return {
    release,
    stopCamera: () => stopCaptureTracks(captureOwner, true)
  }
}
